<script setup>
import { shallowRef, computed } from 'vue'
import { isPlainObject } from 'lodash-es'
import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-community/styles/ag-grid.css' // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css' // Optional Theme applied to the grid
import 'ag-grid-enterprise'

const props = defineProps({
  columnDefs: { type: Array, required: true }, // Array of column groups with children
  rowData: { type: Array, required: true },
  columnTypes: { type: Object, default: () => ({}) }, // Custom column types for styling
  rowHeight: { type: Number }, // Default row height
  autoSizeStrategy: { type: Object }, // Custom autoSizeStrategy for column resizing
  tooltipShowDelay: { type: Number, default: 200 }, // Delay before showing tooltips
  initialState: { type: Object }, // Initial state for the grid (e.g., column visibility, sorting)
  pagination: { type: Boolean, default: true },
  tooltipInteraction: { type: Boolean, default: true },
  context: { type: Object, default: () => ({}) },
  getRowId: { type: Function, default: undefined },
})

const emit = defineEmits(['exportData', 'selectionChanged'])

const agGridApi = shallowRef(null)

const onGridReady = (params) => {
  agGridApi.value = params.api
}

const defaultAutoSizeStrategy = {
  skipHeader: true,
  type: 'fitCellContents',
}

const autoSizeStrategy = computed(() => (props.autoSizeStrategy ? props.autoSizeStrategy : defaultAutoSizeStrategy))

const autoSizeAllColumns = () => {
  if (!agGridApi.value) return
  // console.log('Auto-sizing all columns with strategy:', autoSizeStrategy.value)
  agGridApi.value.autoSizeAllColumns(autoSizeStrategy.value)
}

const autoSizeColumns = (colIds) => {
  if (!agGridApi.value) return
  // console.log('Auto-sizing columns:', colIds)
  agGridApi.value.autoSizeColumns(colIds, {
    skipHeader: autoSizeStrategy.value?.skipHeader ?? defaultAutoSizeStrategy.skipHeader,
  })
}

const gridOptions = computed(() => ({
  suppressRowClickSelection: true,
  suppressCellFocus: true,
  popupParent: document.body,
  autoSizeStrategy: autoSizeStrategy.value,
  animateRows: false,
}))

const defaultColDef = {
  suppressHeaderContextMenu: true,
  suppressHeaderFilterButton: true,
  suppressHeaderMenuButton: true,
}

const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',

      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        // suppressColumnFilter: false,
        // suppressColumnSelectAll: false,
        // suppressColumnExpandAll: true,
      },
    },
    // {
    //       id: "filters",
    //       labelDefault: "Filters",
    //       labelKey: "filters",
    //       iconKey: "filter",
    //       toolPanel: "agFiltersToolPanel",
    //     },
  ],
  // defaultToolPanel: 'columns',
}

const sanitizeCol = (col) => {
  const sanitizeGroup = (parent) => {
    const colGroup = parent?.getColGroupDef()
    const headerName = colGroup?.headerName
    const unit = colGroup?.headerGroupComponentParams?.unit
    if (!headerName) return null

    return {
      headerName: headerName,
      unit: unit,
      parent: parent?.parent ? sanitizeGroup(parent?.parent) : null,
    }
  }

  if (!col) return null
  return {
    colDef: col?.getColDef(),
    parent: col?.parent ? sanitizeGroup(col?.parent) : null,
  }
}

const validateRawValue = (value) => {
  // Only include rawValue if it's a plain object (because we need it to pass object.link only)
  if (!isPlainObject(value)) return null

  const link = value?.link

  if (typeof link !== 'string' || !link.trim()) return value

  try {
    return {
      ...value,
      link: new URL(link, window.location.origin).href,
    }
  } catch {
    return value
  }
}

const normalizeClasses = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string') return value.split(' ').filter(Boolean)
  return []
}

const buildCellParams = ({ col, rowNode, rawValue }) => {
  const colDef = col?.getColDef()
  return {
    value: rawValue,
    data: rowNode?.data,
    node: rowNode,
    rowIndex: rowNode?.rowIndex,
    column: col,
    colDef,
    api: agGridApi.value,
  }
}

const getCellClass = ({ col, rowNode, rawValue }) => {
  const params = buildCellParams({ col, rowNode, rawValue })

  const colDef = params?.colDef

  const cellClassRules = colDef?.cellClassRules
  const classes = []

  if (typeof colDef.cellClass === 'function') {
    classes.push(...normalizeClasses(colDef.cellClass(params)))
  } else {
    classes.push(...normalizeClasses(colDef.cellClass))
  }

  if (cellClassRules) {
    Object.entries(cellClassRules).forEach(([className, rule]) => {
      const matched = typeof rule === 'function' ? rule(params) : false
      if (matched) {
        classes.push(...className.split(' ').filter(Boolean))
      }
    })
  }

  return classes
}

const getCellStyle = ({ col, rowNode, rawValue }) => {
  const params = buildCellParams({ col, rowNode, rawValue })
  const colDef = params?.colDef

  if (typeof colDef.cellStyle === 'function') {
    return colDef.cellStyle(params)
  }

  return colDef?.cellStyle ?? null
}

const getTableState = () => {
  if (!agGridApi.value) return {}
  const selectedNodes = agGridApi.value.getSelectedNodes()
  const allNodes = []

  if (!selectedNodes?.length) {
    agGridApi.value.forEachNodeAfterFilterAndSort((node) => allNodes.push(node))
  }

  const columns = agGridApi.value.getAllDisplayedColumns()
  const sanitizedColumns = columns.map((col) => sanitizeCol(col))
  const nodesForExport = (selectedNodes?.length > 0 ? selectedNodes : allNodes).map((rowNode) => {
    const visibleColumnData = columns.map((col) => {
      const colKey = col.getColId()
      const formattedValue = agGridApi.value.getCellValue({ colKey, rowNode, useFormatter: true })
      const rawValue = agGridApi.value.getCellValue({ colKey, rowNode, useFormatter: false })
      const cellClasses = getCellClass({ col, rowNode, rawValue })
      const cellStyle = getCellStyle({ col, rowNode, rawValue })

      return {
        column: col.getColId(),
        formattedValue,
        rawValue: validateRawValue(rawValue),
        cellClasses,
        cellStyle,
      }
    })

    return {
      rowIndex: rowNode.rowIndex,
      chemical: rowNode?.data?.chemical,
      visibleColumnData,
    }
  })

  return { result: nodesForExport, columns: sanitizedColumns }
}

const onStateUpdated = (params) => {
  // console.log('State updated', params.state)
}

const onSelectionChanged = (params) => {
  emit('selectionChanged', params.api.getSelectedNodes())
}

const resetRowHeights = () => {
  if (agGridApi.value) {
    agGridApi.value.resetRowHeights()
  }
}

const refreshCells = (opts = {}) => {
  if (agGridApi.value) agGridApi.value.refreshCells(opts)
}

defineExpose({
  getTableState,
  resetRowHeights,
  refreshCells,
  autoSizeAllColumns,
  autoSizeColumns,
})
</script>

<template>
  <ag-grid-vue
    :column-defs="columnDefs"
    :column-types="columnTypes"
    :row-data="rowData"
    :get-row-id="getRowId"
    :row-height="rowHeight ?? undefined"
    :default-col-def="defaultColDef"
    :grid-options="gridOptions"
    :context="context"
    :tooltip-show-delay="tooltipShowDelay"
    :tool-panel-params="{ suppressPivots: true, suppressPivotMode: true }"
    @grid-ready="onGridReady"
    :initial-state="initialState ?? undefined"
    @state-updated="onStateUpdated"
    :tooltip-interaction="tooltipInteraction"
    row-selection="multiple"
    :side-bar="sideBar"
    :pagination="pagination"
    @selectionChanged="onSelectionChanged"
    class="ag-theme-quartz"
  />
</template>

<!-- :getRowHeight="() => rowHeight" -->

<style scoped>
.ag-theme-quartz {
  --ag-grid-size: 4px;
  --ag-font-size: 12px;
}

/* .ag-theme-quartz { */
/* --ag-header-cell-text-color: var(--ag-foreground-color); */
/* --ag-header-cell-padding: 12px 4px; */
/* } */
</style>

<!-- <div class="d-flex col-auto text-wrap flex-row justify-content-start align-items-start">
      <div>
        
      </div>
      <div>
        <v-btn-toggle dense>
          <ExportMenu
            :export-url="`${SERVICES_URL}/export_json_report`"
            :ready-report="tableState"
            :show-images="true"
            :ignore-formats="['SMI']"
            :file-name="'test_epi_export'"
            title="Export EPI suite report"
          />
        </v-btn-toggle>

        {{ rowData }} 
 <pre style="overflow-wrap: anywhere; white-space: pre-wrap">{{ columnDefs }}</pre> -->
